import {
  loadMarketIntelligenceMock,
  MARKET_INTELLIGENCE_USE_MOCK_DATA,
  MarketplaceIntelligenceBaseApiClient
} from '../marketplace-intelligence/client';
import type { FactorInteractionsResponse } from '@interfaces/Analytics.interface';
import type {
  ApproveSeedPayload,
  ClassifyScreenPayload,
  ClientReportPayload,
  CreateStudyPayload,
  DiscoveryLogResponse,
  OverrideFactorWeightsPayload,
  RecomputeMlResponse,
  ResumeStudyResponse,
  Study,
  StudyConvergenceResponse,
  StudyReport,
  StudyRun,
  UpdateStudyPayload
} from '@interfaces/Study.interface';

export class ApiStudies extends MarketplaceIntelligenceBaseApiClient {
  async list(params?: {
    product?: string;
    status?: string;
    limit?: number;
  }): Promise<Study[]> {
    if (MARKET_INTELLIGENCE_USE_MOCK_DATA) {
      const mock = await loadMarketIntelligenceMock();
      return mock.listMockStudies(params);
    }
    return this.get<Study[]>('/api/v1/studies', { params });
  }

  async getById(id: string): Promise<Study> {
    if (MARKET_INTELLIGENCE_USE_MOCK_DATA) {
      const mock = await loadMarketIntelligenceMock();
      return mock.getMockStudy(id);
    }
    return this.get<Study>(`/api/v1/studies/${encodeURIComponent(id)}`);
  }

  async create(payload: CreateStudyPayload): Promise<Study> {
    if (MARKET_INTELLIGENCE_USE_MOCK_DATA) {
      const mock = await loadMarketIntelligenceMock();
      return mock.createMockStudy(payload);
    }
    return this.post<Study>('/api/v1/studies', payload);
  }

  async update(id: string, payload: UpdateStudyPayload): Promise<Study> {
    if (MARKET_INTELLIGENCE_USE_MOCK_DATA) {
      const mock = await loadMarketIntelligenceMock();
      return mock.updateMockStudy(id, payload);
    }
    return this.patch<Study>(
      `/api/v1/studies/${encodeURIComponent(id)}`,
      payload
    );
  }

  async remove(id: string): Promise<void> {
    if (MARKET_INTELLIGENCE_USE_MOCK_DATA) {
      const mock = await loadMarketIntelligenceMock();
      mock.deleteMockStudy(id);
      return;
    }
    return this.delete(`/api/v1/studies/${encodeURIComponent(id)}`);
  }

  /** `factors` are the sweep factors chosen in sweep set-up; empty sweeps all. */
  execute(id: string, factors: string[] = []): Promise<Study> {
    return this.post<Study>(
      `/api/v1/studies/${encodeURIComponent(id)}/execute`,
      { factors }
    );
  }

  cancel(id: string): Promise<Study> {
    return this.post<Study>(`/api/v1/studies/${encodeURIComponent(id)}/cancel`);
  }

  /** Re-runs product discovery; the study goes back to `discovering`. */
  refreshSchema(id: string): Promise<Study> {
    return this.post<Study>(
      `/api/v1/studies/${encodeURIComponent(id)}/refresh-schema`
    );
  }

  generateSeed(id: string): Promise<Study> {
    return this.post<Study>(
      `/api/v1/studies/${encodeURIComponent(id)}/generate-seed`
    );
  }

  approveSeed(id: string, payload: ApproveSeedPayload): Promise<Study> {
    return this.post<Study>(
      `/api/v1/studies/${encodeURIComponent(id)}/approve-seed`,
      payload
    );
  }

  /**
   * The API answers 409 (`detail.fixed_rating_factors`) when some rating factors
   * are fixed; re-call with `acknowledgeFixed` once the user has confirmed.
   */
  approveSchema(id: string, acknowledgeFixed = false): Promise<Study> {
    return this.post<Study>(
      `/api/v1/studies/${encodeURIComponent(id)}/approve-schema`,
      undefined,
      acknowledgeFixed ? { params: { acknowledge_fixed: true } } : undefined
    );
  }

  generateScreen(id: string): Promise<Study> {
    return this.post<Study>(
      `/api/v1/studies/${encodeURIComponent(id)}/generate-screen`
    );
  }

  classifyScreen(id: string, payload?: ClassifyScreenPayload): Promise<Study> {
    return this.post<Study>(
      `/api/v1/studies/${encodeURIComponent(id)}/classify-screen`,
      payload
    );
  }

  approveScreen(id: string): Promise<Study> {
    return this.post<Study>(
      `/api/v1/studies/${encodeURIComponent(id)}/approve-screen`
    );
  }

  approveExecution(id: string): Promise<Study> {
    return this.post<Study>(
      `/api/v1/studies/${encodeURIComponent(id)}/approve-execution`
    );
  }

  overrideFactorWeights(
    id: string,
    payload: OverrideFactorWeightsPayload
  ): Promise<Study> {
    return this.post<Study>(
      `/api/v1/studies/${encodeURIComponent(id)}/override-factor-weights`,
      payload
    );
  }

  resume(id: string): Promise<ResumeStudyResponse> {
    return this.post<ResumeStudyResponse>(
      `/api/v1/studies/${encodeURIComponent(id)}/resume`
    );
  }

  resubmit(id: string): Promise<Study> {
    return this.post<Study>(
      `/api/v1/studies/${encodeURIComponent(id)}/resubmit`
    );
  }

  /**
   * Refits the surrogate models on existing quotes. In GCP this only dispatches
   * the ML job (`{ status: 'dispatched' }`); metrics land on the study later.
   */
  recomputeMl(
    id: string,
    runInBackground = false
  ): Promise<RecomputeMlResponse> {
    return this.post<RecomputeMlResponse>(
      `/api/v1/studies/${encodeURIComponent(id)}/recompute-ml`,
      undefined,
      runInBackground ? { params: { run_in_background: true } } : undefined
    );
  }

  async getRuns(id: string): Promise<StudyRun[]> {
    if (MARKET_INTELLIGENCE_USE_MOCK_DATA) {
      const mock = await loadMarketIntelligenceMock();
      return mock.getMockRuns(id);
    }
    return this.get<StudyRun[]>(
      `/api/v1/studies/${encodeURIComponent(id)}/runs`
    );
  }

  runs(id: string): Promise<StudyRun[]> {
    return this.getRuns(id);
  }

  async getReport(id: string): Promise<StudyReport> {
    if (MARKET_INTELLIGENCE_USE_MOCK_DATA) {
      const mock = await loadMarketIntelligenceMock();
      return mock.getMockReport(id);
    }
    return this.get<StudyReport>(
      `/api/v1/studies/${encodeURIComponent(id)}/report`
    );
  }

  report(id: string): Promise<StudyReport> {
    return this.getReport(id);
  }

  async getClientReport(id: string, brand: string): Promise<StudyReport> {
    if (MARKET_INTELLIGENCE_USE_MOCK_DATA) {
      const mock = await loadMarketIntelligenceMock();
      return mock.getMockClientReport(id, brand);
    }
    const payload: ClientReportPayload = { target_brand: brand };
    return this.post<StudyReport>(
      `/api/v1/studies/${encodeURIComponent(id)}/client-report`,
      payload
    );
  }

  clientReport(id: string, brand: string): Promise<StudyReport> {
    return this.getClientReport(id, brand);
  }

  async downloadReportDocx(id: string): Promise<Blob> {
    if (MARKET_INTELLIGENCE_USE_MOCK_DATA) {
      const mock = await loadMarketIntelligenceMock();
      const md = mock.getMockReport(id).markdown ?? '';
      return new Blob([md], {
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      });
    }
    return this.get<Blob>(
      `/api/v1/studies/${encodeURIComponent(id)}/report/docx`,
      { responseType: 'blob' }
    );
  }

  reportDocx(id: string): Promise<Blob> {
    return this.downloadReportDocx(id);
  }

  async getBrands(id: string): Promise<string[]> {
    if (MARKET_INTELLIGENCE_USE_MOCK_DATA) {
      const mock = await loadMarketIntelligenceMock();
      return mock.CYCLE_BRANDS;
    }
    return this.get<string[]>(
      `/api/v1/studies/${encodeURIComponent(id)}/brands`
    );
  }

  brands(id: string): Promise<string[]> {
    return this.getBrands(id);
  }

  async getFactorInteractions(id: string): Promise<FactorInteractionsResponse> {
    if (MARKET_INTELLIGENCE_USE_MOCK_DATA) {
      const mock = await loadMarketIntelligenceMock();
      return mock.getMockFactorInteractions(id);
    }
    return this.get<FactorInteractionsResponse>(
      `/api/v1/studies/${encodeURIComponent(id)}/factor-interactions`
    );
  }

  factorInteractions(id: string): Promise<FactorInteractionsResponse> {
    return this.getFactorInteractions(id);
  }

  getDiscoveryLog(
    id: string,
    params?: { limit?: number; after?: string }
  ): Promise<DiscoveryLogResponse> {
    return this.get<DiscoveryLogResponse>(
      `/api/v1/studies/${encodeURIComponent(id)}/discovery-log`,
      { params }
    );
  }

  getConvergence(id: string): Promise<StudyConvergenceResponse> {
    return this.get<StudyConvergenceResponse>(
      `/api/v1/studies/${encodeURIComponent(id)}/convergence`
    );
  }
}

export { studiesQueryKeys } from './query-keys';
export * from './use-studies-queries';
export * from './use-studies-mutations';
export * from './use-study-action';
export { ApiStudies as StudiesApi };
